<!-- components/Card.svelte -->
<script>
  export let variant = "glass"; // glass, light, dark
  export let size = "normal"; // compact, normal, large
  export let alignment = "left"; // left, center, right
  export let hoverable = true;

  $: classes = `card card--${variant} card--${size} card--${alignment} ${hoverable ? "card--hoverable" : ""}`;
</script>

<div class={classes}>
  {#if $$slots.icon}
    <div class="card-icon">
      <slot name="icon" />
    </div>
  {/if}

  {#if $$slots.title}
    <div class="card-title">
      <slot name="title" />
    </div>
  {/if}

  {#if $$slots.description}
    <div class="card-description">
      <slot name="description" />
    </div>
  {/if}

  {#if $$slots.default}
    <div class="card-content">
      <slot />
    </div>
  {/if}
</div>

<style lang="scss">
  .card {
    border-radius: 1rem;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.1),
        transparent
      );
      transition: left 0.5s ease;
    }

    &--hoverable:hover::before {
      left: 100%;
    }
  }

  /* Variants */
  .card--glass {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);

    &.card--hoverable:hover {
      transform: translateY(-5px);
      border-color: rgba(210, 165, 82, 0.3);
      box-shadow: 0 10px 30px rgba(210, 165, 82, 0.15);
    }

    .card-title {
      color: var(--text-color1);
    }

    .card-description {
      color: var(--text-color2);
      opacity: 0.9;
    }
  }

  .card--light {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.2);

    &.card--hoverable:hover {
      transform: translateY(-3px);
      border-color: rgba(210, 165, 82, 0.4);
      box-shadow: 0 8px 25px rgba(210, 165, 82, 0.15);
      background: rgba(255, 255, 255, 0.9);
    }

    .card-title {
      color: #2c1810;
    }

    .card-description {
      color: #5d4a3a;
    }
  }

  .card--dark {
    background: var(--card-bg);
    border: 1px solid #260d28;
    box-shadow:
      0 1px 2px rgba(255, 255, 255, 0.03) inset,
      0 -1px 2px rgba(0, 0, 0, 0.2) inset,
      0 8px 20px rgba(0, 0, 0, 0.3);

    &.card--hoverable:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
    }

    .card-title {
      color: var(--text-color1);
    }

    .card-description {
      color: var(--text-color2);
    }
  }

  /* Sizes */
  .card--compact {
    padding: 1.5rem;
  }

  .card--normal {
    padding: 2rem;
  }

  .card--large {
    padding: 3rem;
  }

  /* Alignment */
  .card--left {
    text-align: left;
  }

  .card--center {
    text-align: center;
  }

  .card--right {
    text-align: right;
  }

  /* Card content */
  .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    margin-top: 0;
    line-height: 1.3;
  }

  .card-description {
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
  }

  .card-content {
    line-height: 1.5;
  }

  /* Card icon styling */
  .card-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    filter: hue-rotate(0deg) saturate(0.7) brightness(1.1);
  }

  .card--hoverable:hover .card-icon {
    filter: none;
    transform: scale(1.1);
  }

  /* Responsive adjustments */
  @media (max-width: 1200px) {
    .card--compact {
      padding: 2rem;
    }

    .card-title {
      font-size: 1.4rem;
    }

    .card-description {
      font-size: 1rem;
    }
  }

  @media (max-width: 768px) {
    .card--normal {
      padding: 1.5rem;
    }

    .card--compact {
      padding: 1.5rem;
    }
  }

  @media (max-width: 480px) {
    .card--compact {
      padding: 1.25rem;
    }

    .card-title {
      font-size: 1.1rem;
      line-height: 1.4;
      margin-bottom: 1rem;
    }

    .card-description {
      font-size: 0.95rem;
      line-height: 1.6;
    }
  }

  @media (max-width: 320px) {
    .card--compact {
      padding: 1rem;
    }

    .card-title {
      font-size: 1rem;
      line-height: 1.5;
      margin-bottom: 0.75rem;
    }

    .card-description {
      font-size: 0.9rem;
      line-height: 1.7;
    }
  }
</style>
